import { memo } from 'react'
import { usePress } from '@react-aria/interactions'

import { Icon } from '~/shared/ui/Icon'

import { SEARCH_INPUT_CLEAR_ICON_SIZE, SEARCH_INPUT_HEIGHT, SEARCH_INPUT_ICON_SIZE } from './config'
import { useDebouncedSearch } from './hooks'
import type { SearchInputProps } from './types'

import styles from './SearchInput.module.scss'

const ClearButton = memo(function ClearButton(props: { onPress: () => void }) {
  const { onPress } = props
  const { pressProps, isPressed } = usePress({ onPress })

  return (
    <button
      className={styles.clear}
      type='button'
      aria-label='Очистить поиск'
      data-pressed={isPressed}
      {...pressProps}
    >
      <Icon name='close' size={SEARCH_INPUT_CLEAR_ICON_SIZE} />
    </button>
  )
})

/**
 * Специализация TextInput с лупой слева, спека 01 §28. Подсказок не показывает.
 *
 * Корень — `<label>` по той же причине, что и в `TextInput`: строка нативного `<input>`
 * занимает ~19px из 46px поля, а `align-items: center` оставляет над ней и под ней полосы,
 * в которых нажимать было не на что. Крестик очистки — `<button>`, то есть интерактивный
 * потомок: активацию лейбла он по спецификации не запускает и очищает поле как раньше.
 */
export const SearchInput = memo(function SearchInput(props: SearchInputProps) {
  const { value, onChange, placeholder, onClear, onSearch } = props

  useDebouncedSearch(value, onSearch)

  return (
    <label className={styles.root} style={{ height: SEARCH_INPUT_HEIGHT }}>
      <Icon name='search' size={SEARCH_INPUT_ICON_SIZE} strokeWidth={1.5} />
      <input
        className={styles.input}
        value={value}
        placeholder={placeholder}
        aria-label={placeholder ?? 'Поиск'}
        onChange={(event) => onChange(event.target.value)}
      />
      {onClear && value !== '' && <ClearButton onPress={onClear} />}
    </label>
  )
})
